import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Filter,
  ChevronRight,
  Calendar,
  User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorrectiveActions = () => {
  const { isAuditor } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', resolution_notes: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    try {
      const response = await axios.get(`${API}/corrective-actions`);
      setActions(response.data);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAction = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/corrective-actions/${selectedAction.id}`, updateForm);
      toast.success('Action updated');
      fetchActions();
      setShowUpdateDialog(false);
      setSelectedAction(null);
    } catch (error) {
      toast.error('Failed to update action');
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateDialog = (action) => {
    setSelectedAction(action);
    setUpdateForm({ status: action.status, resolution_notes: action.resolution_notes || '' });
    setShowUpdateDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-red-100 text-red-700 border-0">Open</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-100 text-yellow-700 border-0">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700 border-0">Completed</Badge>;
      case 'verified':
        return <Badge className="bg-blue-100 text-blue-700 border-0">Verified</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return <Badge className="bg-red-500 text-white">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500 text-white">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500 text-white">Medium</Badge>;
      case 'low':
        return <Badge className="bg-slate-500 text-white">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'completed' || status === 'verified') return false;
    return new Date(dueDate) < new Date();
  };

  const filteredActions = actions.filter(action => 
    statusFilter === 'all' || action.status === statusFilter
  );

  const stats = {
    open: actions.filter(a => a.status === 'open').length,
    in_progress: actions.filter(a => a.status === 'in_progress').length,
    completed: actions.filter(a => a.status === 'completed').length,
    verified: actions.filter(a => a.status === 'verified').length,
    overdue: actions.filter(a => isOverdue(a.due_date, a.status)).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="corrective-actions-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900">Corrective Actions</h1>
        <p className="text-slate-500 mt-1">Track and manage non-conformance resolutions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="card-hover cursor-pointer" onClick={() => setStatusFilter('open')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.open}</p>
                <p className="text-sm text-slate-500">Open</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer" onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.in_progress}</p>
                <p className="text-sm text-slate-500">In Progress</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer" onClick={() => setStatusFilter('completed')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover cursor-pointer" onClick={() => setStatusFilter('verified')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.verified}</p>
                <p className="text-sm text-slate-500">Verified</p>
              </div>
              <CheckCircle className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={`card-hover ${stats.overdue > 0 ? 'border-red-200 bg-red-50' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {stats.overdue}
                </p>
                <p className="text-sm text-slate-500">Overdue</p>
              </div>
              <AlertTriangle className={`w-8 h-8 ${stats.overdue > 0 ? 'text-red-300' : 'text-slate-200'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
              </SelectContent>
            </Select>
            {statusFilter !== 'all' && (
              <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>
                Clear filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions List */}
      {filteredActions.length > 0 ? (
        <div className="space-y-4">
          {filteredActions.map((action) => (
            <Card 
              key={action.id}
              className={`card-hover ${isOverdue(action.due_date, action.status) ? 'border-red-200' : ''}`}
              data-testid={`action-card-${action.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(action.status)}
                      {getPriorityBadge(action.priority)}
                      {isOverdue(action.due_date, action.status) && (
                        <Badge className="bg-red-500 text-white">Overdue</Badge>
                      )}
                    </div>
                    <p className="text-slate-900 font-medium">{action.description}</p>
                    <div className="flex items-center gap-6 mt-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {action.assigned_to}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Due: {formatDate(action.due_date)}
                      </span>
                    </div>
                    {action.resolution_notes && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                        <strong>Resolution:</strong> {action.resolution_notes}
                      </div>
                    )}
                  </div>
                  {isAuditor() && (
                    <Button
                      variant="outline"
                      onClick={() => openUpdateDialog(action)}
                      className="shrink-0"
                      data-testid={`update-action-${action.id}`}
                    >
                      Update
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">No actions found</h3>
            <p className="text-slate-500 mt-1">
              {statusFilter !== 'all' 
                ? 'No actions match the selected filter'
                : 'All corrective actions will appear here'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Update Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Corrective Action</DialogTitle>
            <DialogDescription>
              Update the status and add resolution notes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="form-label">Status</Label>
              <Select 
                value={updateForm.status} 
                onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}
              >
                <SelectTrigger data-testid="update-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Resolution Notes</Label>
              <Textarea
                placeholder="Describe the actions taken to resolve this issue..."
                value={updateForm.resolution_notes}
                onChange={(e) => setUpdateForm({ ...updateForm, resolution_notes: e.target.value })}
                rows={4}
                data-testid="update-notes-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary"
                onClick={handleUpdateAction}
                disabled={updating}
                data-testid="update-action-submit-btn"
              >
                {updating ? 'Updating...' : 'Update Action'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CorrectiveActions;
